import { DndContext, DragEndEvent, type UniqueIdentifier } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { useEffect, useRef, type Dispatch } from "react"

import { useDidUpdate } from "@/hooks/useDidUpdate"

type Value = { id: string; key: string; name: string }

interface DraggableTableProps<R = Value> {
  disabled?: boolean
  state: [R[], Dispatch<(v: R[]) => R[] | R[]>]
  data: any
  children: React.ReactNode
  action: (data: R[]) => void
  onReorderOrder: (value: {
    name: string | undefined
    id: UniqueIdentifier
  }) => void
}

export const DraggableTable = <R extends Value>(
  props: DraggableTableProps<R>
) => {
  const { disabled, state, data, children, action, onReorderOrder } = props

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [dataSource, setDataSource] = state

  useEffect(() => {
    if (data) {
      setDataSource(data)
    }
  }, [data])

  useDidUpdate(() => {
    if (data?.length !== dataSource?.length) return
    if (data === dataSource) return

    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
      action(dataSource)
    }, 0)
  }, [dataSource])

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((previous) => {
        const activeIndex = previous.findIndex((i) => i.key === active.id)
        const overIndex = previous.findIndex((i) => i.key === over?.id)

        return arrayMove(previous, activeIndex, overIndex)
      })
    }

    const { name } = dataSource.find((i) => i.key === active.id) || {}

    onReorderOrder({ name, id: active.id })
  }

  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext
        disabled={disabled}
        items={dataSource.map((item) => item.key)}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  )
}
